import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { groupBy } from "ramda";
import React, { useMemo, useState } from "react";

import { FEATURE_FLAG_SKIP_ENLIGHTEN_PRO_LICENSE_CHECK } from "../../../../common/features";
import { useFeatureFlagEnabled } from "../../../../common/posthog";
import { checkUserLicenses, UserLicenseStatus } from "../../../../data/queries/userLicense";
import { logError } from "../../../../utils/error";
import { useAuth } from "../../../auth/AuthContext";
import { LICENSE_UNAVAILABLE_MESSAGE } from "../../constants/license";
import { AnalysisProfile } from "../../types";
import {
  parseEmailsFromText,
  shouldBlockSubmission,
  validateEmails,
  ValidationError
} from "../../utils/emailValidation";

export interface BulkAddSummary {
  skippedNoLicense: string[];
  skippedNotFound: string[];
  failedLookup: string[];
}

interface FilterOutcome {
  licensed: string[];
  summary: BulkAddSummary;
}

type LicenseBucket = "licensed" | "skippedNoLicense" | "skippedNotFound" | "failedLookup";

const classifyEmail = (resultByEmail: Map<string, UserLicenseStatus>) =>
  (email: string): LicenseBucket => {
    const result = resultByEmail.get(email.toLowerCase());
    if (!result || result.error) { return "failedLookup"; }
    if (result.hasEnlightenPro) { return "licensed"; }
    if (!result.exists) { return "skippedNotFound"; }
    return "skippedNoLicense";
  };

const groupLicenseResults = (
  emails: string[],
  results: UserLicenseStatus[]
): FilterOutcome | { blocked: true; message: string } => {
  const resultByEmail = new Map(results.map((r) => [r.email.toLowerCase(), r]));
  const buckets = groupBy(classifyEmail(resultByEmail), emails) as Partial<Record<LicenseBucket, string[]>>;

  const licensed = buckets.licensed ?? [];
  const failedLookup = buckets.failedLookup ?? [];
  const skippedNoLicense = buckets.skippedNoLicense ?? [];
  const skippedNotFound = buckets.skippedNotFound ?? [];

  if (licensed.length === 0) {
    const message = failedLookup.length === emails.length
      ? `License check could not be completed for any of the ${emails.length} email${emails.length === 1 ? "" : "s"}. Please try again.`
      : "None of the provided users have an Enlighten Pro license in Spokephone. Add the licenses in the Spokephone APC and try again.";
    return { blocked: true, message };
  }

  return {
    licensed,
    summary: { skippedNoLicense, skippedNotFound, failedLookup }
  };
};

export interface BulkAddPayload {
  emails: string[];
  profileId: string | null;
  status: "active" | "disabled";
  summary: BulkAddSummary;
}

interface BulkAddPeopleDialogProps {
  open: boolean;
  profiles: AnalysisProfile[];
  existingEmails: { email: string; is_deleted: boolean }[];
  onClose: () => void;
  onSave: (payload: BulkAddPayload) => Promise<void>;
}

export const BulkAddPeopleDialog: React.FC<BulkAddPeopleDialogProps> = ({
  open,
  profiles,
  existingEmails,
  onClose,
  onSave
}) => {
  const { tenantId } = useAuth();
  const skipLicenseCheck = useFeatureFlagEnabled(FEATURE_FLAG_SKIP_ENLIGHTEN_PRO_LICENSE_CHECK);
  const [emailText, setEmailText] = useState("");
  const [analysisProfileId, setAnalysisProfileId] = useState("");
  const [status, setStatus] = useState<"active" | "disabled">("active");
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingLicenses, setIsCheckingLicenses] = useState(false);

  const resetDialogState = () => {
    setEmailText("");
    setValidationErrors([]);
    setAnalysisProfileId("");
    setStatus("active");
  };

  const filterLicensed = async (emails: string[]): Promise<FilterOutcome | null> => {
    if (skipLicenseCheck) {
      return {
        licensed: emails,
        summary: { skippedNoLicense: [], skippedNotFound: [], failedLookup: [] }
      };
    }

    if (!tenantId) {
      setValidationErrors([{
        type: "format",
        message: "Unable to verify licenses: missing tenant context."
      }]);
      return null;
    }

    setIsCheckingLicenses(true);
    try {
      const results = await checkUserLicenses(emails, tenantId);
      const grouped = groupLicenseResults(emails, results);

      if ("blocked" in grouped) {
        setValidationErrors([{ type: "format", message: grouped.message }]);
        return null;
      }

      return grouped;
    } catch (error) {
      logError("Bulk license check failed", error);
      setValidationErrors([{
        type: "format",
        message: LICENSE_UNAVAILABLE_MESSAGE
      }]);
      return null;
    } finally {
      setIsCheckingLicenses(false);
    }
  };

  const handleSubmit = async () => {
    const { valid, errors } = validateEmails(emailText, existingEmails);

    const hasNewErrors = errors.length > 0 && validationErrors.length === 0;
    if (shouldBlockSubmission(errors) || hasNewErrors) {
      setValidationErrors(errors);
      return;
    }

    if (valid.length === 0) {
      setValidationErrors([{ type: "format", message: "No valid new email addresses to add" }]);
      return;
    }

    const outcome = await filterLicensed(valid);
    if (outcome === null) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSave({
        emails: outcome.licensed,
        profileId: analysisProfileId || null,
        status,
        summary: outcome.summary
      });
      resetDialogState();
      onClose();
    } catch (error) {
      setValidationErrors([{
        type: "format",
        message: error instanceof Error ? error.message : "Failed to add people"
      }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetDialogState();
      onClose();
    }
  };

  const emailCount = useMemo(() => parseEmailsFromText(emailText).length, [emailText]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Bulk Add People</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {validationErrors.length > 0 && (
            <Box>
              {validationErrors.map((error, index) => (
                <Alert
                  key={index}
                  severity={error.type === "reactivate" ? "info" : "error"}
                  sx={{ mb: 1 }}
                >
                  {error.message}
                </Alert>
              ))}
            </Box>
          )}

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Comma separated list of unique email addresses
            </Typography>
            <TextField
              multiline
              rows={6}
              value={emailText}
              onChange={(e) => {
                setEmailText(e.target.value);
                setValidationErrors([]);
              }}
              placeholder="user1@example.com, user2@example.com, user3@example.com"
              fullWidth
              helperText={emailCount > 0 ? `${emailCount} email address${emailCount !== 1 ? "es" : ""} detected` : ""}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel>Analysis Group</InputLabel>
            <Select
              value={analysisProfileId}
              onChange={(e) => setAnalysisProfileId(e.target.value)}
              label="Analysis Group"
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {profiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as "active" | "disabled")}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting || isCheckingLicenses}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || isCheckingLicenses || !emailText.trim()}
          startIcon={isCheckingLicenses ? <CircularProgress size={16} /> : null}
        >
          {isCheckingLicenses ? "Checking licenses..." : "Bulk Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
